import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Check } from "lucide-react";
import { MaskLines, Reveal } from "@/components/motion";
import { BrandMarquee, SectionLabel } from "@/components/shared";
import { MANIFESTO, COACHES } from "@/data/site";

function PageHero() {
  return (
    <section className="relative overflow-hidden pb-16 pt-40 md:pt-48" data-testid="about-hero">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionLabel>Our Story</SectionLabel>
        <h1 className="font-heading text-6xl uppercase leading-[0.82] tracking-tight text-white sm:text-8xl lg:text-[8rem]">
          <MaskLines lines={["More Than", <>A <span className="text-neon-green">Program</span></>]} />
        </h1>
        <Reveal delay={0.6}>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-white/70 md:text-2xl">
            ARD Midsouth Performance was founded on a simple belief: young players in DeSoto County and
            the greater Memphis area deserve elite-level development close to home.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Story() {
  return (
    <section className="relative z-10 bg-obsidian py-24 md:py-32" data-testid="about-story">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-2 lg:gap-20">
        <Reveal className="flex items-center justify-center">
          <div className="relative flex aspect-[4/5] w-full items-center justify-center overflow-hidden border border-white/10 bg-black">
            <div className="absolute h-2/3 w-2/3 rounded-full bg-neon-green/10 blur-3xl" />
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_ard-elite-soccer/artifacts/4gj67vje_ARD.png"
              alt="ARD Performance Midsouth logo"
              className="relative z-10 w-3/4 max-w-xs object-contain drop-shadow-[0_0_35px_rgba(57,255,20,0.25)]"
            />
          </div>
        </Reveal>
        <div className="flex flex-col justify-center">
          <SectionLabel>The Mission</SectionLabel>
          <Reveal>
            <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-5xl">
              Development First. Always.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-6 space-y-5 font-body text-base leading-relaxed text-white/70 md:text-lg">
              <p>
                <span className="text-white">ARD</span> comes from the Gaelic word meaning "high" or "elevated." But to us,
                it represents something greater: the commitment to continually raise the standard in everything we do,
                both on and off the field.
              </p>
              <p>
                At ARD MidSouth, we believe elite development goes beyond creating better soccer players. We challenge
                our athletes to train with purpose, compete with confidence, think at a higher level, and develop the
                character required to succeed.
              </p>
              <p>
                We see ourselves as more than coaches — we are mentors committed to helping each player elevate their
                game, their mindset, and themselves in pursuit of their highest potential.
              </p>
              <p>
                We built ARD Midsouth Performance to help young players reach their greatest potential and
                realize their dreams on the pitch. Our players train in small groups with intentional,
                game-realistic sessions designed to make every rep count.
              </p>
              <p>
                From first-touch fundamentals for our youngest players to position-specific
                performance training for teens, every level of ARD is designed to move players forward —
                on the ball, in their decisions, and in their competitive mindset.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Philosophy() {
  return (
    <section className="relative z-10 bg-surface py-24 md:py-32" data-testid="about-philosophy">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-16">
          <SectionLabel>Coaching Philosophy</SectionLabel>
          <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">
            How We Develop Players
          </h2>
        </div>
        <div className="space-y-px border-y border-white/10">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.1}>
              <div className="group grid grid-cols-1 items-center gap-6 py-10 transition-colors hover:bg-obsidian md:grid-cols-12 md:px-6">
                <div className="font-heading text-6xl text-stroke md:col-span-2 md:text-7xl">{m.n}</div>
                <h3 className="font-heading text-3xl uppercase leading-none text-white md:col-span-4 md:text-4xl">{m.title}</h3>
                <p className="font-body text-base leading-relaxed text-white/60 md:col-span-6">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Coaches() {
  return (
    <section className="relative z-10 bg-obsidian py-24 md:py-32" data-testid="about-coaches">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-14">
          <SectionLabel>The Staff</SectionLabel>
          <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">Meet The Co-Founders</h2>
          <p className="mt-4 max-w-xl font-body text-sm text-white/50">
            ARD Midsouth Performance is led by two licensed head coaches driving elite player development across the Midsouth.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2">
          {COACHES.map((c, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className="group grid grid-cols-1 gap-6 border border-white/10 p-6 sm:grid-cols-5">
                <div className="overflow-hidden sm:col-span-2">
                  <img src={c.image} alt={c.name} className="aspect-square w-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0" />
                </div>
                <div className="flex flex-col justify-center sm:col-span-3">
                  <span className="mb-3 inline-flex w-fit items-center gap-2 border border-neon-green/50 bg-neon-green/10 px-3 py-1 font-heading text-xs uppercase tracking-widest text-neon-green">
                    <Award size={13} strokeWidth={2.5} /> {c.credential}
                  </span>
                  <h3 className="font-heading text-3xl uppercase leading-none text-white">{c.name}</h3>
                  <div className="mt-1 font-body text-xs uppercase tracking-widest text-white/50">{c.role} • {c.title}</div>
                  <p className="mt-4 font-body text-sm leading-relaxed text-white/60">{c.bio}</p>
                  <ul className="mt-4 space-y-2">
                    {c.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 font-body text-sm text-white/80">
                        <Check size={16} strokeWidth={3} className="mt-0.5 shrink-0 text-neon-green" /> {h}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 space-y-1 border-t border-white/10 pt-4">
                    <a href={`mailto:${c.email}`} className="block font-body text-xs text-white/60 hover:text-neon-green">{c.email}</a>
                    <a href={`tel:${c.phone.replace(/[^0-9]/g, "")}`} className="block font-body text-xs text-white/60 hover:text-neon-green">{c.phone}</a>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Development() {
  const steps = [
    { t: "Assess", d: "Every player is evaluated to understand their current level, strengths and areas to grow." },
    { t: "Train", d: "Targeted, game-realistic sessions in small groups that maximize touches and decisions." },
    { t: "Compete", d: "Players are challenged in competitive environments that translate to real matches." },
    { t: "Elevate", d: "Progress is tracked and standards are raised as players move to their next level." },
  ];
  return (
    <section className="relative z-10 bg-surface py-24 md:py-32" data-testid="about-development">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-14">
          <SectionLabel>The Approach</SectionLabel>
          <h2 className="font-heading text-4xl uppercase leading-none tracking-tight text-white sm:text-6xl">Player Development Path</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08}>
              <div className="h-full border border-white/10 bg-obsidian p-8">
                <span className="font-heading text-5xl text-neon-green">0{i + 1}</span>
                <h3 className="mt-6 font-heading text-2xl uppercase text-white">{s.t}</h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-white/60">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-16 text-center">
          <Link to="/register" data-testid="about-cta-btn" className="group inline-flex items-center gap-2 bg-neon-green px-10 py-5 font-heading text-lg uppercase tracking-widest text-black">
            Start Your Player's Journey
            <ArrowUpRight size={22} strokeWidth={3} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero />
      <BrandMarquee />
      <Story />
      <Philosophy />
      <Coaches />
      <Development />
    </div>
  );
}
